import { cn } from '@/lib/cn';

/**
 * StepIndicator — small dotted progress indicator used across multi-step
 * flows (currently the reading-tracker wizard; reusable beyond it).
 *
 * `current` is 1-indexed: `current=2` means step 2 is active and step 1
 * is done. `total` defaults to 4 (the wizard's step count) but is exposed
 * for other flows that adopt the component later.
 *
 * Visual states map onto the existing OllieReads palette so the indicator
 * doesn't introduce parallel tokens (READING_TRACKER_PLAN.md §7):
 *   - past:    `or-green-700` filled
 *   - current: `or-orange-500` filled
 *   - future:  `or-slate-200` filled
 */
export interface StepIndicatorProps {
  current: number;
  total?: number;
  className?: string;
}

export function StepIndicator({ current, total = 4, className }: StepIndicatorProps) {
  return (
    <ol
      className={cn('flex items-center justify-center gap-2', className)}
      aria-label={`Step ${current} of ${total}`}
    >
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => {
        const isPast = n < current;
        const isCurrent = n === current;
        return (
          <li
            key={n}
            aria-current={isCurrent ? 'step' : undefined}
            aria-label={`Step ${n} of ${total}`}
            className={cn(
              'h-2.5 w-2.5 rounded-full transition-colors',
              isPast && 'bg-or-green-700',
              isCurrent && 'bg-or-orange-500',
              !isPast && !isCurrent && 'bg-or-slate-200',
            )}
          />
        );
      })}
    </ol>
  );
}
